import type { CSSProperties } from "react";
import type { Theme } from "@repo/tokens";
import { hexToRgba } from "@repo/utils";

export const pdfViewerGlobalStyles = `
.react-pdf__Page {
  margin: 0 auto;
  width: max-content;
}

.react-pdf__Page__canvas {
  max-width: none !important;
  height: auto !important;
}
`;

export const getPdfViewerStyles = (theme: Theme, isMobile: boolean) => {
  const modalBorderRadius = isMobile ? 0 : 12;

  return {
    overlay: {
      position: "fixed",
      inset: 0,
      zIndex: 1200,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: theme.spacing.lg,
      background: hexToRgba(theme.colors.neutral[50], 0.55),
    } satisfies CSSProperties,
    modal: {
      position: "relative",
      width: isMobile ? "100vw" : "min(1260px, 96vw)",
      height: isMobile ? "100vh" : "min(88vh, 930px)",
      borderRadius: modalBorderRadius,
      background: theme.colors.neutral[95],
      display: "flex",
      flexDirection: "column",
      padding: theme.spacing.md,
    } satisfies CSSProperties,
    topLoaderTrack: {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: theme.spacing.xs,
      background: theme.colors.neutral[90],
      overflow: "hidden",
      borderTopLeftRadius: modalBorderRadius,
      borderTopRightRadius: modalBorderRadius,
    } satisfies CSSProperties,
    topLoaderBar: {
      width: "0%",
      height: "100%",
      background: theme.colors.primary[40],
      transition: "width 220ms ease",
    } satisfies CSSProperties,
    toolbar: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing.md,
      marginBottom: theme.spacing.md,
    } satisfies CSSProperties,
    toolbarTop: {
      position: "relative",
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      width: "100%",
    } satisfies CSSProperties,
    toolbarTopSpacer: {
      justifySelf: "start",
    } satisfies CSSProperties,
    fileControls: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing.sm,
      position: "absolute",
      left: "50%",
      transform: "translateX(-50%)",
    } satisfies CSSProperties,
    fileText: {
      whiteSpace: "nowrap",
      fontSize: theme.typography.body.large.regular.fontSize,
      fontWeight: theme.typography.body.large.bold.fontWeight,
      color: theme.colors.neutral[10],
    } satisfies CSSProperties,
    toolbarBottom: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "100%",
      gap: theme.spacing.md,
      flexWrap: "nowrap",
    } satisfies CSSProperties,
    centerControls: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: theme.spacing.md,
    } satisfies CSSProperties,
    pageControls: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "flex-start",
      gap: theme.spacing.md,
    } satisfies CSSProperties,
    pageText: {
      display: "flex",
      alignItems: "center",
      flexWrap: "nowrap",
      whiteSpace: "nowrap",
      gap: theme.spacing.sm,
      fontSize: theme.typography.body.large.regular.fontSize,
      fontWeight: theme.typography.body.large.bold.fontWeight,
      color: theme.colors.neutral[10],
    } satisfies CSSProperties,
    inlinePageInput: {
      border: "none",
      background: "transparent",
      color: theme.colors.neutral[10],
      fontSize: "inherit",
      fontWeight: "inherit",
      textAlign: "center",
      width: theme.spacing.md,
      outline: "none",
      padding: 0,
    } satisfies CSSProperties,
    zoomControls: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing.md,
      justifyContent: "center",
      flex: "0 0 auto",
    } satisfies CSSProperties,
    toolbarCloseButton: {
      width: 36,
      height: 36,
      minWidth: 36,
      padding: 0,
      border: "none",
      outline: "none",
      background: "transparent",
      color: theme.colors.neutral[50],
      display: "inline-flex",
      justifySelf: "end",
      marginLeft: "auto",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      flexShrink: 0,
    } satisfies CSSProperties,
    iconButton: {
      width: isMobile ? 28 : 34,
      height: isMobile ? 28 : 34,
      border: "none",
      borderRadius: "50%",
      background: "transparent",
      color: theme.colors.neutral[10],
      fontSize: theme.typography.body.large.regular.fontSize,
      fontWeight: theme.typography.body.large.bold.fontWeight,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
    } satisfies CSSProperties,
    disabledIconButton: {
      opacity: 0.35,
      cursor: "not-allowed",
    } satisfies CSSProperties,
    viewerSurface: {
      position: "relative",
      flex: 1,
      borderRadius: theme.radii.lg,
      background: theme.colors.neutral[10],
      overflowX: "auto",
      overflowY: "auto",
      padding: 14,
    } satisfies CSSProperties,
    pageStack: {
      width: "max-content",
      minWidth: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: theme.spacing.md,
      paddingBottom: theme.spacing.sm,
    } satisfies CSSProperties,
    pageWrap: {
      width: "100%",
      display: "flex",
      justifyContent: "center",
      alignItems: "flex-start",
    } satisfies CSSProperties,
    status: {
      color: theme.colors.neutral[100],
      fontSize: theme.typography.body.large.regular.fontSize,
      fontWeight: theme.typography.body.large.emphasized.fontWeight,
      textAlign: "center",
      paddingTop: theme.spacing["2xl"],
    } satisfies CSSProperties,
    errorOverlay: {
      position: "absolute",
      inset: 0,
      zIndex: 2,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: theme.colors.neutral[90],
      color: theme.colors.neutral[100],
      fontSize: theme.typography.body.large.regular.fontSize,
      fontWeight: theme.typography.body.large.emphasized.fontWeight,
      textAlign: "center",
      padding: theme.spacing.md,
    } satisfies CSSProperties,
  };
};
